import { useNavigate, useRouterState } from '@tanstack/react-router';
import { members } from '@personal-blog/contracts';
import {
  DropdownMenu,
  IcnAlert,
  IcnBell,
  IcnBookOpen,
  IcnBookmark,
  IcnDownload,
  IcnGear,
  IcnFolder,
  IcnHome,
  IcnKey,
  IcnStopwatch,
  IcnTarget,
  IcnUser,
  IcnZap,
  PrimitiveButton,
  useMediaQuery,
  type MenuEntry,
} from '@viquard/doc-ui';
import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLogout, useMe } from '@/features/auth/api';
import { useAuthToast } from '@/features/auth/hooks';
import { useMemberDetail } from '@/features/members/api';
import { MemberAvatar } from '@/features/members/views/member-avatar';

const TOPBAR_NAV_VISIBLE_QUERY = '(min-width: 1024px)';
const ITEM_ICON_SIZE = 14;

export interface UserMenuProps {
  displayName: string;
  email: string | null;
  userId: string | null;
}

type PageItemId =
  | 'dashboard'
  | 'calendar'
  | 'groups'
  | 'library'
  | 'downloads'
  | 'members'
  | 'parent'
  | 'profile'
  | 'tasks'
  | 'notifications'
  | 'guide'
  | 'read-later'
  | 'bookmarks'
  | 'settings'
  | 'admin';

export function UserMenu({ displayName, email, userId }: UserMenuProps): ReactNode {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const logout = useLogout();
  const { notifyError } = useAuthToast();
  const { location } = useRouterState();
  const topbarNavVisible = useMediaQuery(TOPBAR_NAV_VISIBLE_QUERY);
  const ownMember = useMemberDetail(userId ?? undefined);

  const activeId = useMemo<PageItemId | null>(() => {
    const path = location.pathname;
    if (path === '/dashboard') return 'dashboard';
    if (path === '/calendar' || path.startsWith('/calendar/')) return 'calendar';
    if (path === '/settings') return 'settings';
    if (path === '/admin' || path.startsWith('/admin/')) return 'admin';
    if (path === '/groups' || path.startsWith('/groups/')) return 'groups';
    if (path === '/library/downloads') return 'downloads';
    if (path === '/library' || path.startsWith('/library/')) return 'library';
    if (path === '/members') return 'members';
    if (path === '/parent') return 'parent';
    if (path === '/profile' || (userId !== null && path === `/users/${userId}`)) return 'profile';
    if (path === '/tasks') return 'tasks';
    if (path === '/notifications') return 'notifications';
    if (path === '/guide' || path.startsWith('/guide/')) return 'guide';
    if (path === '/read-later') return 'read-later';
    if (path === '/bookmarks') return 'bookmarks';
    return null;
  }, [location.pathname, userId]);

  const me = useMe();
  const showAdminLink = useMemo(() => {
    if (me.data == null) return false;
    const role = me.data.user.role;
    const def = members.ROLE_DEFS[role];
    return def.rank >= members.ROLE_DEFS.manager.rank;
  }, [me.data]);

  const items = useMemo<MenuEntry[]>(() => {
    const entries: MenuEntry[] = [
      {
        kind: 'item',
        id: 'identity',
        searchText: displayName,
        label: (
          <div className="topbar-user-menu-identity">
            <MemberAvatar
              name={displayName}
              design={ownMember.data?.member.avatarDesign ?? null}
              size="sm"
            />
            <span>
              <span className="topbar-user-menu-identity__name">{displayName}</span>
              {email !== null ? (
                <span className="topbar-user-menu-identity__email">{email}</span>
              ) : null}
            </span>
          </div>
        ),
        onSelect: () => {
          void navigate({ to: '/profile' });
        },
      },
    ];

    entries.push({
      kind: 'group',
      id: 'account',
      label: t('userMenu.accountSection'),
      items: [
        {
          kind: 'item',
          id: 'profile',
          label: <PageLabel text={t('userMenu.profile')} active={activeId === 'profile'} />,
          icon: <IcnUser size={ITEM_ICON_SIZE} />,
          onSelect: () => {
            void navigate({ to: '/profile' });
          },
        },
        {
          kind: 'item',
          id: 'tasks',
          label: <PageLabel text={t('userMenu.tasks')} active={activeId === 'tasks'} />,
          icon: <IcnTarget size={ITEM_ICON_SIZE} />,
          onSelect: () => {
            void navigate({ to: '/tasks' });
          },
        },
        {
          kind: 'item',
          id: 'notifications',
          label: (
            <PageLabel
              text={t('userMenu.notifications')}
              active={activeId === 'notifications'}
            />
          ),
          icon: <IcnBell size={ITEM_ICON_SIZE} />,
          onSelect: () => {
            void navigate({ to: '/notifications' });
          },
        },
        {
          kind: 'item',
          id: 'guide',
          label: <PageLabel text={t('userMenu.guide')} active={activeId === 'guide'} />,
          icon: <IcnBookOpen size={ITEM_ICON_SIZE} />,
          onSelect: () => {
            void navigate({ to: '/guide' });
          },
        },
        {
          kind: 'item',
          id: 'settings',
          label: <PageLabel text={t('userMenu.settings')} active={activeId === 'settings'} />,
          icon: <IcnGear size={ITEM_ICON_SIZE} />,
          onSelect: () => {
            void navigate({ to: '/settings' });
          },
        },
      ],
    });

    if (!topbarNavVisible) {
      entries.push(
        { kind: 'separator', id: 'sep-1' },
        {
          kind: 'group',
          id: 'navigation',
          label: t('userMenu.navigationSection'),
          items: [
            {
              kind: 'item',
              id: 'dashboard',
              label: <PageLabel text={t('userMenu.dashboard')} active={activeId === 'dashboard'} />,
              icon: <IcnHome size={ITEM_ICON_SIZE} />,
              onSelect: () => {
                void navigate({ to: '/dashboard' });
              },
            },
            {
              kind: 'item',
              id: 'groups',
              label: <PageLabel text={t('userMenu.groups')} active={activeId === 'groups'} />,
              icon: <IcnUser size={ITEM_ICON_SIZE} />,
              onSelect: () => {
                void navigate({ to: '/groups' });
              },
            },
            {
              kind: 'item',
              id: 'calendar',
              label: <PageLabel text={t('userMenu.calendar')} active={activeId === 'calendar'} />,
              icon: <IcnStopwatch size={ITEM_ICON_SIZE} />,
              onSelect: () => {
                void navigate({ to: '/calendar' });
              },
            },
            {
              kind: 'item',
              id: 'library',
              label: <PageLabel text={t('userMenu.library')} active={activeId === 'library'} />,
              icon: <IcnFolder size={ITEM_ICON_SIZE} />,
              onSelect: () => {
                void navigate({ to: '/library' });
              },
            },
          ],
        },
      );
    }

    entries.push(
      { kind: 'separator', id: 'sep-2' },
      {
        kind: 'group',
        id: 'reading',
        label: t('userMenu.readingSection'),
        items: [
          {
            kind: 'item',
            id: 'read-later',
            label: <PageLabel text={t('userMenu.readLater')} active={activeId === 'read-later'} />,
            icon: <IcnBookOpen size={ITEM_ICON_SIZE} />,
            onSelect: () => {
              void navigate({ to: '/read-later' });
            },
          },
          {
            kind: 'item',
            id: 'bookmarks',
            label: <PageLabel text={t('userMenu.bookmarks')} active={activeId === 'bookmarks'} />,
            icon: <IcnBookmark size={ITEM_ICON_SIZE} />,
            onSelect: () => {
              void navigate({ to: '/bookmarks' });
            },
          },
          {
            kind: 'item',
            id: 'downloads',
            label: <PageLabel text={t('userMenu.downloads')} active={activeId === 'downloads'} />,
            icon: <IcnDownload size={ITEM_ICON_SIZE} />,
            onSelect: () => {
              void navigate({ to: '/library/downloads' });
            },
          },
        ],
      },
      { kind: 'separator', id: 'sep-3' },
      {
        kind: 'group',
        id: 'site',
        label: t('userMenu.siteSection'),
        items: [
          {
            kind: 'item',
            id: 'members',
            label: <PageLabel text={t('userMenu.members')} active={activeId === 'members'} />,
            icon: <IcnUser size={ITEM_ICON_SIZE} />,
            onSelect: () => {
              void navigate({ to: '/members' });
            },
          },
          {
            kind: 'item',
            id: 'parent',
            label: <PageLabel text={t('userMenu.parent')} active={activeId === 'parent'} />,
            icon: <IcnKey size={ITEM_ICON_SIZE} />,
            onSelect: () => {
              void navigate({ to: '/parent' });
            },
          },
          ...(showAdminLink
            ? [
                {
                  kind: 'item' as const,
                  id: 'admin',
                  label: <PageLabel text={t('userMenu.admin')} active={activeId === 'admin'} />,
                  icon: <IcnAlert size={ITEM_ICON_SIZE} />,
                  onSelect: () => {
                    void navigate({ to: '/admin' });
                  },
                },
              ]
            : []),
        ],
      },
      { kind: 'separator', id: 'sep-5' },
      {
        kind: 'item',
        id: 'logout',
        label: t('userMenu.logout'),
        icon: <IcnZap size={ITEM_ICON_SIZE} />,
        tone: 'danger',
        onSelect: () => {
          logout.mutate(undefined, {
            onSuccess: () => {
              void navigate({ to: '/' });
            },
            onError: (err) => {
              notifyError(err);
            },
          });
        },
      },
    );

    return entries;
  }, [
    activeId,
    displayName,
    email,
    logout,
    navigate,
    notifyError,
    ownMember.data?.member.avatarDesign,
    showAdminLink,
    t,
    topbarNavVisible,
  ]);

  return (
    <DropdownMenu items={items} placement="bottom-end" className="topbar-user-menu-panel">
      <PrimitiveButton
        aria-label={t('userMenu.label')}
        className="topbar-user-menu inline-flex items-center"
        style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
      >
        <MemberAvatar
          name={displayName}
          design={ownMember.data?.member.avatarDesign ?? null}
          size="sm"
        />
      </PrimitiveButton>
    </DropdownMenu>
  );
}

function PageLabel({ text, active }: { text: string; active: boolean }): ReactNode {
  if (!active) return text;
  return <span className="user-menu-link--active">{text}</span>;
}
